import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "../../context/OnboardingContext";
import { COMPANY_DOMAIN, AUTH_REGISTER_WITH_COGNITO } from "../../lib/API_KEYS";
import { ApiHelper } from "../../utils/ApiHelper";
import { toast, ToastContainer } from "react-toastify";

const roleSubRolesMap = {
  Buyer: ["Procurement", "Collaborator", "Technical", "Finance"],
  Seller: ["Technical", "Finance", "Sales"],
};

export default function TeamMembers() {
  const navigate = useNavigate();
  const { cognitoData, setCognitoData } = useOnboarding();
  const [members, setMembers] = useState(
    cognitoData.team || [
      { email: "", role: roleSubRolesMap[cognitoData.role]?.[0] || "", companyName: cognitoData.companyName || "" },
    ]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (idx, field, value) => {
    const updated = [...members];
    updated[idx][field] = value;
    setMembers(updated);
  };

  const addMember = () => {
    setMembers([
      ...members,
      { email: "", role: roleSubRolesMap[cognitoData.role]?.[0] || "", companyName: cognitoData.companyName || "" },
    ]);
  };

  const removeMember = (idx) => {
    setMembers(members.filter((_, i) => i !== idx));
  };

  const onNext = async () => {
    if (isSubmitting) return; // prevent double click
    setIsSubmitting(true);
    try {
      // Save team and mark remindLater as false
      setCognitoData((prev) => ({ ...prev, team: members, remindLater: false }));

      const response = await new ApiHelper().FetchFromServer(
        COMPANY_DOMAIN[0].replace(":domain", cognitoData.email.trim().toLowerCase().split("@")[1]),
        COMPANY_DOMAIN[1],
      );

      if (response?.data?.domain) {
        setIsSubmitting(false);
        return toast.error("This company domain is already registered. Please contact your company admin to get access");
      }

      console.log("eieie", cognitoData);

      await new ApiHelper().FetchFromServer(
        AUTH_REGISTER_WITH_COGNITO[0],
        AUTH_REGISTER_WITH_COGNITO[1],
        undefined,
        {
          email: cognitoData.email,
          password: cognitoData.password,
          name: cognitoData.fullName || "",
          role: cognitoData.role || "Buyer",
          companyName: cognitoData.companyName || "",
        },
        { Authorization: "Bearer" } // prevent ApiHelper from requiring an existing session
      );

      // Redirect to Email Verification
      navigate("/email-verification");
    } catch (err) {
      console.error("TeamMembers submission error:", err);
      toast.error(err.message || "Error signing up. Please try again.");
      setIsSubmitting(false);
    }
  };

  const onBack = async () => {
    if (isSubmitting) return; // prevent double click
    setIsSubmitting(true);
    try {
      // Mark remindLater as true
      const updatedData = { ...cognitoData, team: members, remindLater: true };
      setCognitoData(updatedData);

      await new ApiHelper().FetchFromServer(
        AUTH_REGISTER_WITH_COGNITO[0],
        AUTH_REGISTER_WITH_COGNITO[1],
        undefined,
        {
          email: updatedData.email,
          password: updatedData.password,
          name: updatedData.fullName || "",
          role: updatedData.role || "Buyer",
          companyId: updatedData.companyId,
          companyName: updatedData.companyName || "",
        },
        { Authorization: "Bearer" }
      );

      navigate("/email-verification");
    } catch (err) {
      console.error("TeamMembers remindLater error:", err);
      alert(err.message || "Error signing up. Please try again.");
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setCognitoData((prev) => ({ ...prev, team: members }));
  }, [members]);

  return (
    <>
      <div className="d-flex vh-100">
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <div className="container-fluid d-flex flex-column" style={{ minHeight: "100vh", paddingLeft: "60px", paddingRight: "30px", maxWidth: "600px" }}>
            <div className="mb-4 pt-5">
              <img src="/images/Logo.png" alt="Logo" style={{ height: "50px" }} />
            </div>

            <div style={{ maxWidth: "500px" }}>
              <h2 className="fw-bold mb-1">Who else is on your team?</h2>
              <p className="text-muted" style={{ fontSize: "14px" }}>
                Help us tailor the best experience for you
              </p>
            </div>

            <div className="mt-4" style={{ maxWidth: "600px" }}>
              {members.map((member, idx) => (
                <div className="mb-3" key={idx}>
                  <div className="d-flex merged-input align-items-center position-relative">
                    <input
                      type="email"
                      className="form-control border-2 custom-focus border-end-0 rounded-0"
                      placeholder="Enter email"
                      value={member.email}
                      onChange={(e) => handleChange(idx, "email", e.target.value)}
                      style={{ width: "83%" }}
                    />
                    <select
                      className="form-select border-start-1 bg-light text-dark custom-focus rounded-0 ps-2"
                      value={member.role}
                      onChange={(e) => handleChange(idx, "role", e.target.value)}
                      style={{ width: "30%" }}
                    >
                      {roleSubRolesMap[cognitoData.role]?.map((role, i) => (
                        <option key={i} value={role}>{role}</option>
                      ))}
                    </select>
                    {idx > 0 && (
                      <span className="remove-btn" onClick={() => removeMember(idx)}>
                        &#10005;
                      </span>
                    )}
                  </div>
                </div>
              ))}
              <div className="mt-2">
                <span className="add-another" onClick={addMember}>
                  <img src="/images/plus.svg" alt="+" className="me-1" /> Add another
                </span>
              </div>
            </div>

            <div className="mt-auto d-flex gap-3 py-4">
              <button
                className="btn btn-outline-dark btn-sm"
                style={{ height: "38px", width: "180px", fontWeight: "500" }}
                onClick={onBack}
                disabled={isSubmitting}
              >
                Remind me later
              </button>
              <button
                className="btn btn-primary btn-sm"
                style={{ backgroundColor: "#1A1A52", border: "none", height: "38px", width: isSubmitting ? "150px" : "100px", fontWeight: "500", opacity: isSubmitting ? 0.7 : 1 }}
                onClick={onNext}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Signing up..." : "Continue"}
              </button>
            </div>
          </div>
        </div>

        <div className="d-none d-md-flex align-items-center justify-content-center" style={{ width: "50%" }}>
          <img src="/images/BG.png" alt="Side" className="img-fluid" style={{ maxHeight: "90%", maxWidth: "90%", objectFit: "contain" }} />
        </div>
      </div>
      <ToastContainer />
    </>
  );
}
